/**
 * VP-18720 / PH-940: login email domain validation for the practice settings
 * (Change Login Email, Add Provider / Invite Practice Member).
 *
 * Two layers, in this order:
 *  1. Client: the TLD must be in the IANA root zone list (`tlds` package), so
 *     "yahoo.con" is rejected as soon as the field blurs, even if the backend
 *     is slow or unavailable.
 *  2. Backend, trans v1 POST /valogin/checkEmailDomain: DNS check (NXDOMAIN,
 *     no MX/A/AAAA, null MX) plus the typo suggestion. The suggestion rules
 *     live there (mailcheck fork + filter), so they are not duplicated here.
 *
 * Fails open: if the backend call errors or times out, only the client TLD
 * verdict counts. The backend validates again on submit.
 *
 * Mirrors va-portal's src/utils/emailDomainValidation.js (signup pages).
 */
import axios from 'axios';
import { computed, nextTick, ref } from 'vue';
import IANA_TLDS from 'tlds';
import useUtilities from './UseUtilities';
import { t } from '@/i18n.js';

export const EMAIL_DOMAIN_ERROR_KEY = "This email address doesn't look right. Please check it for typos.";

/**
 * Submit-time blocking (VP-18719): every route that creates or changes a login
 * email answers a bad address with HTTP 400 carrying `error_code` (one of
 * these), `message` and `suggestion`, on top of the route's usual 400 body.
 * Pass INLINE_EMAIL_ERRORS as axios config so requestV1 skips its toast; the
 * form shows the rejection in the field's notice instead (see `reject`).
 */
export const EMAIL_REJECTION_CODES = ['INVALID_EMAIL_DOMAIN', 'INVALID_EMAIL_FORMAT'];
export const INLINE_EMAIL_ERRORS = { silentErrorCodes: EMAIL_REJECTION_CODES };
export const isEmailRejection = (body) => EMAIL_REJECTION_CODES.includes(body?.error_code);

const CLOUD_SERVICE_BASE_URL = 'https://api.vibrant-wellness.com/v1';

// Its own quiet client: the check is best effort, so it must not go through the
// shared clients' error notifications or token-refresh handling.
const emailDomainService = axios.create({
  baseURL: CLOUD_SERVICE_BASE_URL + (process.env.VUE_APP_ENV === 'production'
    ? '/portal/trans-service'
    : '/portal/trans-service-st'),
  timeout: 8000,
});

const TLD_SET = new Set(IANA_TLDS);

// Checks still in flight, so a Save clicked right after typing can wait for
// them: the library input reports blur from a document click listener, which
// runs after the Save button's own click handler.
const pendingChecks = new Set();
function track(promise) {
  pendingChecks.add(promise);
  promise.finally(() => pendingChecks.delete(promise));
  return promise;
}

/**
 * For a Save handler: resolves once every email domain check has settled,
 * including one started by the very click that triggered the save.
 *
 * That click reaches the Save button's handler first; the library input only
 * reports blur afterwards, from its own document click listener, and then on
 * Vue's next flush. Browsers run pending promise callbacks between listeners,
 * so awaiting a microtask here would come back before that listener has even
 * run. Yield a full task instead, then one flush, then wait.
 */
export async function settleEmailDomainChecks() {
  await new Promise((resolve) => setTimeout(resolve, 0));
  await nextTick();
  await Promise.all([...pendingChecks]);
}

/**
 * True when the email's TLD is a real IANA TLD. Returns true for input it
 * cannot parse, because format errors belong to the existing format check.
 */
export function hasValidTld(email) {
  const domain = String(email || '').trim().split('@').pop();
  if (!domain || !domain.includes('.')) return true;
  const tld = domain.split('.').pop().toLowerCase();
  if (!tld) return true;
  return TLD_SET.has(tld);
}

/**
 * Resolves (never rejects) to { email, valid, messageKey, suggestion }.
 * valid is true, false, or null when it could not be decided (backend
 * unavailable and the TLD is fine). Only valid === false blocks.
 * Response: { valid, code, message, suggestion }, code one of
 * OK | INVALID_FORMAT | INVALID_TLD | DOMAIN_NOT_FOUND | NO_MAIL_SERVER.
 */
export async function checkEmailDomain(email) {
  const trimmed = String(email || '').trim();
  const tldOk = hasValidTld(trimmed);
  let data = null;
  try {
    const res = await emailDomainService.post('/valogin/checkEmailDomain', { email: trimmed });
    data = res?.data || null;
  } catch (err) {
    console.warn('Email domain check unavailable, falling back to the TLD check', err?.message || err);
  }
  const suggestion = data?.suggestion && data.suggestion !== trimmed ? data.suggestion : null;
  if (!tldOk) {
    return { email: trimmed, valid: false, messageKey: EMAIL_DOMAIN_ERROR_KEY, suggestion };
  }
  if (!data || typeof data.valid !== 'boolean') {
    return { email: trimmed, valid: null, messageKey: '', suggestion: null };
  }
  return {
    email: trimmed,
    valid: data.valid,
    // the backend sends the ticket copy; keep the locale key so it translates
    messageKey: data.valid ? '' : EMAIL_DOMAIN_ERROR_KEY,
    suggestion,
  };
}

/**
 * Per-field state. `getEmail` returns the field's current value.
 * A result only counts while the field still holds the email it was run for,
 * and a slow reply for an older value is ignored.
 */
export default function useEmailDomainCheck(getEmail) {
  const { validateEmail } = useUtilities();
  const result = ref(null);
  let latest = 0;

  const trimmedEmail = computed(() => String(getEmail() || '').trim());
  const current = computed(() => (result.value && result.value.email === trimmedEmail.value ? result.value : null));
  const isInvalid = computed(() => current.value?.valid === false);
  const suggestion = computed(() => current.value?.suggestion || '');
  /**
   * What the field's notice box shows. The suggested address is the box's
   * clickable action, so it reads "...Did you mean name@yahoo.com".
   *  - domain invalid: error, with the suggestion when there is one
   *  - domain fine but a likely typo (name@gmial.com): alert, does not block
   * Messages are translated here: the error key contains periods, so
   * InputQuestion's `SettingPage.${key}` dot-path lookup cannot resolve it.
   */
  const notice = computed(() => {
    const result = current.value;
    if (!result) return null;
    if (result.valid === false) {
      return result.suggestion
        ? { state: 'error', message: t('SettingPage["Email domain suggestion with error"]'), action: result.suggestion }
        : { state: 'error', message: t(`SettingPage["${result.messageKey}"]`), action: '' };
    }
    return result.suggestion
      ? { state: 'alert', message: t('SettingPage["Email domain suggestion"]'), action: result.suggestion }
      : null;
  });

  // Resolves to the result for the current email, or null when there is nothing to check.
  async function check() {
    const email = trimmedEmail.value;
    if (!email || !validateEmail(email)) {
      result.value = null;
      return null;
    }
    if (current.value) return current.value;
    const token = ++latest;
    const next = await track(checkEmailDomain(email));
    if (token !== latest) return current.value;
    result.value = next;
    return current.value;
  }

  /**
   * Show a submit-time rejection (`body` = the route's 400 body) exactly like a
   * failed blur check, so the error and suggestion land in the same notice.
   */
  function reject(email, body) {
    latest += 1; // drop any blur check still in flight
    result.value = {
      email: String(email || '').trim(),
      valid: false,
      messageKey: body?.error_code === 'INVALID_EMAIL_FORMAT' ? 'Please enter a valid Email' : EMAIL_DOMAIN_ERROR_KEY,
      suggestion: body?.suggestion || null,
    };
  }

  return { isInvalid, notice, suggestion, check, reject };
}

/**
 * The same check for a comma-separated list of emails (Additional Notification
 * Email). Every address is checked; the notice points at the first one the
 * domain check blocks, otherwise at the first likely typo. `applySuggestion`
 * returns the list with that one address replaced.
 */
export function useEmailListDomainCheck(getText) {
  const { validateEmail } = useUtilities();
  const result = ref(null);
  let latest = 0;

  const text = computed(() => String(getText() || ''));
  const emails = () => text.value.split(',').map((e) => e.trim()).filter(Boolean);
  const current = computed(() => (result.value && result.value.text === text.value ? result.value : null));
  const blocked = computed(() => current.value?.items.find((item) => item.valid === false) || null);
  const isInvalid = computed(() => !!blocked.value);
  const notice = computed(() => {
    const bad = blocked.value;
    if (bad) {
      return bad.suggestion
        ? { state: 'error', message: t('SettingPage["Email domain suggestion with error"]'), action: bad.suggestion, from: bad.email }
        : { state: 'error', message: t('SettingPage["Invalid email: ${email}"]', { email: bad.email }), action: '', from: bad.email };
    }
    const typo = current.value?.items.find((item) => item.suggestion);
    return typo
      ? { state: 'alert', message: t('SettingPage["Email domain suggestion"]'), action: typo.suggestion, from: typo.email }
      : null;
  });

  async function check() {
    const list = emails();
    // format problems are the list field's own validation; nothing to check yet
    if (!list.length || list.some((email) => !validateEmail(email))) {
      result.value = null;
      return null;
    }
    if (current.value) return current.value;
    const forText = text.value;
    const token = ++latest;
    const items = await track(Promise.all(list.map((email) => checkEmailDomain(email))));
    if (token !== latest) return current.value;
    result.value = { text: forText, items };
    return current.value;
  }

  function applySuggestion() {
    const n = notice.value;
    if (!n?.action) return text.value;
    return text.value.split(',').map((email) => (email.trim() === n.from ? n.action : email)).join(',');
  }

  return { isInvalid, notice, check, applySuggestion };
}
